import { useState } from 'react';

export interface AnalyseItem {
  id: string;
  label: string;
}

export const CATEGORIES_ANALYSES: { nom: string; items: AnalyseItem[] }[] = [
  {
    nom: 'Échographies',
    items: [
      { id: 'echo-pelvienne', label: 'Pelvienne' },
      { id: 'echo-vesico-prostatique', label: 'Vésico-prostatique' },
      { id: 'echo-obstetricale', label: 'Obstétricale' },
      { id: 'echo-abdominale', label: 'Abdominale' },
      { id: 'echo-abdomino-pelvienne', label: 'Abdomino-pelvienne' },
      { id: 'echo-testiculaire', label: 'Testiculaire' },
      { id: 'echo-doppler-cardiaque', label: 'Doppler cardiaque' },
      { id: 'echo-renale', label: 'Rénale' },
      { id: 'echo-doppler-veineux', label: 'Doppler veineux' },
      { id: 'echo-hepatique', label: 'Hépatique' },
      { id: 'echo-parties-molles', label: 'Des parties molles' },
      { id: 'echo-mammaires', label: 'Mammaires' },
      { id: 'echo-thyroidienne', label: 'Thyroïdienne' },
      { id: 'echo-cervicale', label: 'Cervicale' },
      { id: 'echo-ecg', label: 'ECG' },
      { id: 'echo-transfontanelle', label: 'Transfontanelle' },
    ],
  },
  {
    nom: 'Analyses (Paraclinique)',
    items: [
      { id: 'ana-nfs', label: 'NFS (Numération Formule Sanguine)' },
      { id: 'ana-creat', label: 'Créat (Créatinine)' },
      { id: 'ana-calcemie', label: 'Calcémie' },
      { id: 'ana-mg', label: 'Mg++ (Magnésium)' },
      { id: 'ana-ionogramme', label: 'Ionogramme' },
      { id: 'ana-chol-t', label: 'Chol T (Cholestérol total)' },
      { id: 'ana-hdl', label: 'HDL' },
      { id: 'ana-ldl', label: 'LDL' },
      { id: 'ana-transa', label: 'Transa (Transaminases)' },
      { id: 'ana-trigly', label: 'Trigly (Triglycérides)' },
      { id: 'ana-hiv', label: 'HIV' },
      { id: 'ana-aghbs', label: 'AgHBs' },
      { id: 'ana-urice', label: 'Urice (Acide urique)' },
      { id: 'ana-gly', label: 'Gly (Glycémie)' },
      { id: 'ana-tpha', label: 'TPHA' },
      { id: 'ana-vdrl', label: 'VDRL' },
      { id: 'ana-sdw', label: 'SDW' },
      { id: 'ana-ge-dp', label: 'GE+DP' },
      { id: 'ana-ecbu', label: 'ECBU' },
      { id: 'ana-crp', label: 'CRP' },
      { id: 'ana-cpk', label: 'CPK' },
      { id: 'ana-tbg', label: 'TBG' },
      { id: 'ana-tc', label: 'TC' },
      { id: 'ana-ts', label: 'TS' },
      { id: 'ana-tck', label: 'TCK' },
      { id: 'ana-aslo', label: 'ASLO' },
      { id: 'ana-fs-dp', label: 'FS+DP' },
      { id: 'ana-akop', label: 'AKOP' },
      { id: 'ana-atb', label: 'ECBU+ATB' },
      { id: 'ana-fs', label: 'FS' },
      { id: 'ana-uree', label: 'Urée' },
      { id: 'ana-gamma-gt', label: 'Gamma GT' },
      { id: 'ana-te', label: 'TE' },
      { id: 'ana-tp-inr', label: 'TP-INR' },
      { id: 'ana-vs', label: 'VS' },
      { id: 'ana-icg', label: 'ICG' },
      { id: 'ana-d-dimeres', label: 'D-Dimères' },
      { id: 'ana-gsrh', label: 'GSRh' },
      { id: 'ana-tsh', label: 'TSH' },
      { id: 'ana-electrophorese-hb', label: 'Électrophorèse Hb' },
      { id: 'ana-glucosurie', label: 'Glucosurie' },
    ],
  },
];

interface AnalyseChecklistProps {
  selectedIds: string[];
  onToggle: (id: string) => void;
  accentColor: string;
  extraCategories?: { nom: string; items: AnalyseItem[] }[];
}

const ACCENTS: Record<string, { summaryBox: string; summaryText: string; chip: string; badge: string; item: string; checkbox: string }> = {
  teal: {
    summaryBox: 'bg-teal-50 border-teal-200',
    summaryText: 'text-teal-800',
    chip: 'bg-teal-100 text-teal-700 hover:bg-teal-200',
    badge: 'bg-teal-600',
    item: 'bg-teal-50 border border-teal-200',
    checkbox: 'text-teal-600 focus:ring-teal-500',
  },
  pink: {
    summaryBox: 'bg-pink-50 border-pink-200',
    summaryText: 'text-pink-800',
    chip: 'bg-pink-100 text-pink-700 hover:bg-pink-200',
    badge: 'bg-pink-600',
    item: 'bg-pink-50 border border-pink-200',
    checkbox: 'text-pink-600 focus:ring-pink-500',
  },
  blue: {
    summaryBox: 'bg-clinic-blue/10 border-clinic-blue/30',
    summaryText: 'text-clinic-blue-dark',
    chip: 'bg-clinic-blue/15 text-clinic-blue-dark hover:bg-clinic-blue/25',
    badge: 'bg-clinic-blue',
    item: 'bg-clinic-blue/10 border border-clinic-blue/30',
    checkbox: 'text-clinic-blue focus:ring-clinic-blue/40',
  },
};

export default function AnalyseChecklist({ selectedIds, onToggle, accentColor, extraCategories = [] }: AnalyseChecklistProps) {
  const [categoriesOuvertes, setCategoriesOuvertes] = useState<Record<string, boolean>>({
    'Échographies': true,
    'Analyses (Paraclinique)': true,
  });

  const allCategories = [...CATEGORIES_ANALYSES, ...extraCategories];
  const a = ACCENTS[accentColor] || ACCENTS.teal;

  const toggleCategorie = (nom: string) => {
    setCategoriesOuvertes(prev => ({ ...prev, [nom]: !prev[nom] }));
  };

  const getSelectedCountForCategory = (categorieNom: string) => {
    const categorie = allCategories.find(c => c.nom === categorieNom);
    if (!categorie) return 0;
    return categorie.items.filter(item => selectedIds.includes(item.id)).length;
  };

  const getTotalSelected = () => selectedIds.length;

  const getSelectedLabels = () => {
    const allItems = allCategories.flatMap(c => c.items);
    return selectedIds.map(id => {
      const item = allItems.find(i => i.id === id);
      return item ? item.label : id;
    });
  };

  return (
    <div className="space-y-4">
      {/* Résumé de la sélection */}
      {getTotalSelected() > 0 && (
        <div className={`p-3 rounded-lg border ${a.summaryBox}`}>
          <p className={`text-sm font-medium ${a.summaryText}`}>
            {getTotalSelected()} analyse{getTotalSelected() > 1 ? 's' : ''} sélectionnée{getTotalSelected() > 1 ? 's' : ''} :
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {getSelectedLabels().map((label, idx) => (
              <span
                key={idx}
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap ${a.chip}`}
                onClick={() => {
                  const itemId = allCategories.flatMap(c => c.items).find(i => i.label === label)?.id;
                  if (itemId) onToggle(itemId);
                }}
              >
                {label}
                <i className="ri-close-line ml-1"></i>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Catégories */}
      {allCategories.map((categorie) => {
        const isOpen = categoriesOuvertes[categorie.nom];
        const selectedInCat = getSelectedCountForCategory(categorie.nom);
        const totalInCat = categorie.items.length;

        return (
          <div key={categorie.nom} className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
            {/* En-tête de catégorie cliquable */}
            <button
              type="button"
              onClick={() => toggleCategorie(categorie.nom)}
              className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <i className={`${isOpen ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'} text-gray-500 text-lg`}></i>
                <h3 className="text-base font-semibold text-gray-800">{categorie.nom}</h3>
                {selectedInCat > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-xs font-bold text-white ${a.badge}`}>
                    {selectedInCat}
                  </span>
                )}
              </div>
              <span className="text-xs text-gray-400">{totalInCat} examens</span>
            </button>

            {/* Grille d'items */}
            {isOpen && (
              <div className="px-5 pb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                  {categorie.items.map((item) => (
                    <label
                      key={item.id}
                      className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg cursor-pointer transition-colors ${
                        selectedIds.includes(item.id)
                          ? a.item
                          : 'hover:bg-white border border-transparent'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(item.id)}
                        onChange={() => onToggle(item.id)}
                        className={`w-4 h-4 rounded border-gray-300 cursor-pointer ${a.checkbox}`}
                      />
                      <span className={`text-sm ${selectedIds.includes(item.id) ? 'font-medium text-gray-900' : 'text-gray-600'}`}>
                        {item.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}